<template>
  <b-navbar ref="navbar" toggleable="md" :class="navbar_classes" fixed="top">
    <b-navbar-toggle target="nav_collapse" />

    <b-collapse id="nav_collapse" is-nav>
      <b-navbar-brand
        class="text-blue"
        to="/"
        active-class=""
        exact-active-class=""
      >
        <img
          id="synaplus_logo"
          src="../assets/synaplus_logo_square_outlinev2.svg"
          class="d-inline-block align-top"
          alt="Synaplus"
          :style="synaplus_logo_style"
        />
        Cosmo
      </b-navbar-brand>

      <b-navbar-nav class="navbar-inverse">
        <b-nav-item to="/" :exact="true">
          <span class="fas fa-project-diagram text-success" />{{
            $t('projects')
          }}</b-nav-item
        >

        <b-nav-item
          v-if="!is_project_independent_path"
          :disabled="true"
          class="d-none d-md-block"
        >
          <span class="fal fa-angle-right" />
        </b-nav-item>

        <b-nav-item
          v-if="!is_project_independent_path"
          :to="workflow_path"
          :active="in_workflow"
        >
          <span class="fas fa-pencil text-primary" /> Workflow
        </b-nav-item>

        <b-nav-item v-if="in_brick" :disabled="true" class="d-none d-md-block">
          <span class="fal fa-angle-right" />
        </b-nav-item>

        <b-nav-item v-if="workflow_path" :active="in_brick">
          <i :class="brick_name_logo[1]" /> {{ brick_name_logo[0] }}
        </b-nav-item>
      </b-navbar-nav>

      <b-navbar-nav
        v-if="critical_errors.length !== 0"
        class="flex-w-stretch bg-red"
      >
        <transition name="criticalerror" mode="out-in">
          <b-nav-text
            :key="critical_error_text"
            class="text-white font-weight-bold text-uppercase mx-1"
            >{{ critical_error_text }}</b-nav-text
          >
        </transition>
      </b-navbar-nav>

      <b-navbar-nav class="ml-auto">
        <b-nav-form>
          <b-button
            v-show="is_fullscreen"
            class="mr-1"
            variant="outline-primary"
            :pressed="is_fullscreen"
            size="sm"
            @click="toggle_fullscreen"
          >
            <span class="fas fa-compress-arrows-alt" />
          </b-button>

          <project-in-production-indicator
            :is_in_production="cur_project.is_in_production"
          />

          <project-powered-button
            v-show="workflow_path"
            class="mr-1"
            :class="show_if_in_project"
            :value="cur_project"
            :disabled="cur_project.is_in_production"
          />

          <b-button
            id="platformpower"
            class="mr-1"
            :variant="platform_powered_variant"
            :pressed="platform_powered_pressed"
            size="sm"
            @click="toggle_platform_powered"
          >
            <span :class="platform_powered_icon" />
          </b-button>

          <b-tooltip
            ref="powertooltip"
            target="platformpower"
            placement="bottomright"
            class="text-left"
            :show.sync="platformtooltipshown"
          >
            <p>{{ $t('activates_deactivates_platform') }}</p>
            <p>
              <b-button variant="primary" :pressed="false" size="sm">
                <span class="fas fa-spinner fa-fw fa-spin" />
              </b-button>
              {{ $t('computing_cosmo') }}
            </p>
            <p>
              <b-button variant="success" :pressed="false" size="sm">
                <span class="fas fa-battery-full fa-fw" />
              </b-button>
              {{ $t('activated_idle_cosmo') }}
            </p>
            <p>
              <b-button variant="outline-danger" :pressed="false" size="sm">
                <span class="fas fa-battery-slash fa-fw" />
              </b-button>
              {{ $t('paused_cosmo') }}
            </p>
          </b-tooltip>

          <b-dropdown
            class="mr-1"
            variant="light"
            size="sm"
            no-caret
            right
            toggle-class="p-0"
          >
            <template slot="button-content"
              ><span class="fa-stack">
                <span class="fas fa-circle fa-stack-2x text-blue"/>
                <span class="fas fa-cog fa-stack-1x fa-inverse fa-fw"/></span
            ></template>

            <b-dropdown-item @click="$router.push('/admin')">{{
              $t('platform_administration')
            }}</b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item
              :disabled="project_id === -1"
              @click="show_project_admin"
              >{{ $t('project_administration') }}</b-dropdown-item
            >
            <b-dropdown-item
              :disabled="project_id === -1"
              @click="show_project_dashboard"
              >{{ $t('project_dashboard') }}</b-dropdown-item
            >
            <b-dropdown-item
              :disabled="project_id === -1"
              @click="show_warnings"
              >{{ $t('show_warnings') }}</b-dropdown-item
            >

            <b-dropdown-item
              :disabled="project_id === -1"
              @click="export_project"
              >{{ $t('export_project') }}</b-dropdown-item
            >

            <b-dropdown-item
              :disabled="project_id === -1"
              @click="import_project"
              >{{ $t('import_project') }}</b-dropdown-item
            >

            <b-dropdown-item
              v-b-modal.project-snapshots-modal
              :disabled="project_id === -1"
              >{{ $t('show_snapshots') }}</b-dropdown-item
            >
            <b-dropdown-divider></b-dropdown-divider>

            <b-dropdown-item @click="reboot_webserver">{{
              $t('reboot_webserver')
            }}</b-dropdown-item>

            <b-dropdown-item
              v-b-modal.system-info-modal
              @click="get_all_infos"
              >{{ $t('system_info') }}</b-dropdown-item
            >

            <b-dropdown-item v-b-modal.license-modal @click="get_license">{{
              $t('show_license')
            }}</b-dropdown-item>
          </b-dropdown>

          <b-dropdown
            variant="light"
            size="sm"
            no-caret
            right
            toggle-class="p-0"
          >
            <template slot="button-content"
              ><span class="fa-stack">
                <span class="fas fa-circle fa-stack-2x text-blue"/>
                <span class="fas fa-user fa-stack-1x fa-inverse fa-fw"/></span
            ></template>
            <b-dropdown-item disabled>
              {{
                _.isEmpty(cur_user)
                  ? 'Username'
                  : client_decrypt_string(cur_user.username)
              }}
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>

            <b-dropdown-form>
              <b-form-select v-model="locale">
                <option value="en">English (United States)</option>
                <option value="fr">Français (France)</option>
              </b-form-select>
            </b-dropdown-form>

            <b-dropdown-item @click="$router.push(`/user/admin`)">{{
              $t('preferences')
            }}</b-dropdown-item>
            <b-dropdown-item @click="toggle_fullscreen">{{
              $t('fullscreen')
            }}</b-dropdown-item>

            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item @click="logout">{{
              $t('disconnect')
            }}</b-dropdown-item>
          </b-dropdown>
        </b-nav-form>
      </b-navbar-nav>
    </b-collapse>
  </b-navbar>
</template>

<style></style>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import numeral from 'numeral'

import { client_encryption_mixin, project_mixin } from '@/components/mixin.js'

import {
  set_power,
  set_project_power,
  reboot_webserver,
  get_license,
  logout,
} from '@/api/subscriptions.js'

import ProjectInProductionIndicator from '@/components/project_in_production_indicator.vue'
import ProjectPoweredButton from '@/components/project_powered_button.vue'

import {
  definition_from_name as brick_definition_from_name,
  navbar_from_name,
} from '@/components/workflow_common.js'

// *****************************************************************************

export default {
  name: 'Navbar',
  components: {
    ProjectPoweredButton,
    ProjectInProductionIndicator,
  },
  mixins: [client_encryption_mixin, project_mixin],
  props: [],
  data() {
    return {
      critical_error_text: '',
      platformtooltipshown: false,
    }
  },

  computed: {
    /*    project_id() {
      let project_id = -1
      let route = this.$route.path
      // let regexp = new RegExp('\/[^/]+\/(\\d*)')
      let regexp = new RegExp('\/projects\/(\\d*)')
      let match = route.match(regexp)
      if (match) {
        project_id = parseInt(match[1], 10)
      }
      return project_id
    },
    cur_project() {
      let res =
        (this.automl_projects_undeleted_object.projects || {})[
          this.project_id
        ] || {}
      return res
    },*/
    _() {
      return _
    },
    my_client_enc_key() {
      let res = this.client_encryption_key
      if (_.isUndefined(res)) {
        res = 'undefined:('
      }
      return res
    },
    cur_user() {
      let res =
        (this.automl_users_undeleted_object.users || {})[
          this.authenticated_user_id
        ] || {}
      return res
    },
    // configuration_path() {
    //   let res
    //   // N.B PL : This is weird, when we enter /projects, the url is redirected to /projects/ and therefore
    //   // we have access to restart / export project when no project is selected
    //   let route = this.$route.path
    //   // console.log('route : ', route)
    //   if (_.includes(['/', '/projects'], route)) {
    //     res = '/configuration'
    //   }
    //   // console.log('res : ', res)
    //   return res
    // },
    brick_name_logo() {
      let res

      let route = this.$route.path

      let regexp = new RegExp('/projects/(\\d*)/bricks/(\\d*)')
      let match = route.match(regexp)
      let workflow_object = {}
      let navbar_name = ''
      if (match) {
        let project_id = match[1]
        let brick_id = match[2]
        let brick = this.automl_bricks_undeleted_object_per_project(project_id)
          .bricks[brick_id]
        workflow_object =
          brick_definition_from_name((brick || {}).brick_name) || {}
        navbar_name = navbar_from_name((brick || {}).brick_name)
      }
      let logo = _.cloneDeep(workflow_object.front_classes) || []
      logo[logo.length - 1] = (workflow_object.back_classes || [])[2]
      // return [this.$t(workflow_object.navbar_name), logo]
      return [navbar_name, logo]
    },

    in_workflow() {
      let route = this.$route.path
      let regexp = new RegExp('/projects/(\\d*)/workflow')
      let match = route.match(regexp)
      let res = match !== null

      return res
    },
    in_brick() {
      let route = this.$route.path
      let regexp = new RegExp('/projects/(\\d*)/bricks/(\\d*)')
      let match = route.match(regexp)
      let res = match !== null

      return res
    },
    navbar_classes() {
      let classes = 'bg-white app-navbar text-blue'.split(' ')
      let margin_class
      margin_class = 'py-0'
      classes.push(margin_class)
      return classes
    },
    synaplus_logo_style() {
      let res
      res = 'height:40px;width:40px;margin-top:-5px;margin-bottom:-5px'
      return res
    },
    is_project_independent_path() {
      let res = false
      let route = this.$route.path
      let regexp = new RegExp('/(admin|projects|user)?$')
      let match = route.match(regexp)
      if (match) {
        res = true
      }
      return res
    },
    workflow_path() {
      let res

      let route = this.$route.path
      let regexp = new RegExp(
        '/projects/(\\d*)/[bricks|administration|import|export|warnings]'
      )
      let match = route.match(regexp)
      if (match) {
        res = `/projects/${match[1]}/workflow`
      }
      return res
    },
    show_if_in_project() {
      // return this.configuration_path ? 'invisible' : 'visible'
      return this.is_project_independent_path ? 'invisible' : 'visible'
    },
    platform_powered_variant() {
      return this.platform_status.is_powered
        ? this.platform_status.is_running
          ? 'primary'
          : 'success'
        : 'outline-danger'
    },
    platform_powered_pressed() {
      return this.platform_status.is_powered
    },
    platform_powered_icon() {
      return this.platform_status.is_powered
        ? this.platform_status.is_running
          ? 'fas fa-spinner fa-fw fa-spin'
          : 'fas fa-battery-full fa-fw'
        : 'fas fa-battery-slash fa-fw'
    },
    locale: {
      get() {
        return this.$store.state.locale
      },
      set(nv) {
        this.set_locale(nv)
      },
    },

    ...mapState(['platform_status', 'is_fullscreen', 'authenticated_user_id']),
    ...mapGetters([
      'automl_bricks_undeleted_object_per_project',
      'automl_projects_undeleted_object',
      'automl_users_undeleted_object',
      'critical_errors',
    ]),
  },

  watch: {
    critical_errors(nv) {
      if (nv.length !== 0 && _.isUndefined(this.critical_error_handler)) {
        this.critical_error_handler = setTimeout(this.set_critical_error)
      }
    },
    locale(val) {
      this.$root.$i18n.locale = val
      // May be the next usage :
      // (c.f. https://momentjs.com/docs/#/customization/calendar/)
      // moment.updateLocale(val)
      moment.locale(val)
      numeral.locale(val)
    },
  },

  async mounted() {
    this.$store.commit('update_locale', 'en')

    this.critical_error_current = 0
    if (this.critical_errors.length !== 0) {
      this.set_critical_error()
    }
  },

  methods: {
    // decrypt(s) {
    //   console.log('Before decrypt', s)
    //   if (_.isUndefined(s)) {
    //     return s
    //   }
    //   console.log(decrypt_str)
    //   console.log(s)
    //   console.log(atob(s))
    //   let res = decrypt_str(s)
    //   console.log('After decrypt', res)
    //   return res
    // },
    toggle_fullscreen() {
      this.$emit('toggle_fullscreen')
    },
    get_all_infos() {
      this.$emit('get_all_infos')
    },
    toggle_project_powered() {
      // TODO : Coherence between frontend/backend ?
      set_project_power(this.project_id, !this.project_powered)
    },
    toggle_platform_powered() {
      this.platformtooltipshown = false
      set_power(!this.platform_status.is_powered)
    },
    show_project_admin() {
      if (!_.isUndefined(this.project_id)) {
        this.$router.push(`/projects/${this.project_id}/admin`)
      }
    },
    show_project_dashboard() {
      if (!_.isUndefined(this.project_id)) {
        this.$router.push(`/projects/${this.project_id}/dashboard`)
      }
    },
    show_warnings() {
      if (!_.isUndefined(this.project_id)) {
        this.$router.push(`/projects/${this.project_id}/warnings`)
      }
    },
    export_project() {
      if (!_.isUndefined(this.project_id)) {
        this.$router.push(`/projects/${this.project_id}/export`)
      }
    },
    import_project() {
      // N.B PL : For now importing project is done like this but this is
      // probably temporary and later you might need to choose which project
      // you want to 'override'
      if (!_.isUndefined(this.project_id)) {
        this.$router.push(`/projects/${this.project_id}/import`)
      }
    },
    hash_project() {
      if (!_.isUndefined(this.project_id)) {
        this.$router.push(`/projects/${this.project_id}/project_hash`)
      }
    },
    set_locale(val) {
      this.$store.commit('update_locale', val)
    },

    async reboot_webserver() {
      await reboot_webserver()
    },
    async get_license() {
      this.license = await get_license()
    },
    async logout() {
      await logout()
      this.$store.commit('set_user_authentication_status', undefined)
      this.$router.push({
        path: '/login',
        query: { redirect: this.$route.path },
      })
    },
    set_critical_error() {
      let critical_error_current = this.critical_error_current
      critical_error_current += 1
      if (critical_error_current >= this.critical_errors.length) {
        critical_error_current = 0
      }
      this.critical_error_current = critical_error_current
      let critical_error_handler = undefined
      if (this.critical_errors.length !== 0) {
        this.critical_error_text = this.$t(
          this.critical_errors[critical_error_current]
        )
        critical_error_handler = setTimeout(this.set_critical_error, 5000)
      }
      this.critical_error_handler = critical_error_handler
    },
  },
}
</script>
